/**
 * Ownership helpers for liabilities (and, later, REO).
 *
 * A debt can be owned by one borrower (sole) or several (joint). We store that
 * as `owners`: an array of borrower-index strings, e.g. ["0"] or ["0","1"].
 * MISMO expresses the same thing as one RELATIONSHIP arc per owner, so this
 * list maps directly to the export/import.
 *
 * `borrower` (a single index) is kept in step as owners[0] for older code that
 * still reads it.
 */
import { MARRIED } from "./marital_status";

/**
 * A borrower's FULL legal name: first, middle initial, last, and suffix. Used on
 * the Debt page and anywhere a credit tradeline is labeled - a name mix-up on a
 * credit report (e.g. a Jr vs a living Sr) is very costly for the LO to fix, so
 * we always show the middle initial and suffix, never just "First Last".
 */
export function borrowerFullName(b) {
  if (!b) return "";
  return [b.first_name, b.middle_name, b.last_name, b.suffix]
    .map(s => (s || "").trim())
    .filter(Boolean)
    .join(" ");
}

/** The spouse's borrower index if married to another borrower in the file, else -1. */
export function spouseIndex(borrowers, i) {
  const b = borrowers[i];
  if (b && b.marital_status === MARRIED) {
    const m = parseInt(b.married_to, 10);
    if (Number.isInteger(m) && m >= 0 && m < borrowers.length && m !== i) {
      return m;
    }
  }
  return -1;
}

/**
 * The borrower "household" for grouping on the Debt page: a married couple is
 * one household (they share a joint credit report), an unmarried borrower is
 * their own. Returns the sorted borrower indices in the household of borrower i.
 * So 2 unmarried borrowers + 1 couple = three separate households.
 */
export function householdIndices(borrowers, i) {
  const sp = spouseIndex(borrowers, i);
  return sp === -1 ? [i] : [i, sp].sort((a, b) => a - b);
}

/** Stable key for a household (e.g. "0,1" or "2"). */
export function householdKey(borrowers, i) {
  return householdIndices(borrowers, i).join(",");
}

/** Full-name label for a household: "Bill Testcase Jr and Mary Smith" or a single name. */
export function householdLabel(borrowers, i) {
  return householdIndices(borrowers, i)
    .map(idx => borrowerFullName(borrowers[idx]))
    .filter(Boolean)
    .join(" and ");
}

/** Normalize any record to its list of owner indices (strings). */
export function getOwners(record) {
  if (record && Array.isArray(record.owners) && record.owners.length > 0) {
    return record.owners.map(String);
  }
  // Fall back to the legacy single-owner field.
  if (record && record.borrower !== undefined && record.borrower !== "") {
    return [String(record.borrower)];
  }
  return [];
}

/** True when a debt is shared by more than one borrower. */
export function isJoint(record) {
  return getOwners(record).length > 1;
}

/**
 * Apply an owners list back onto a record, keeping `borrower` synced to the
 * primary (first) owner. Returns a new object; does not mutate the input.
 */
export function withOwners(record, owners) {
  const list = (owners || []).map(String);
  return {
    ...record,
    owners: list,
    borrower: list.length > 0 ? list[0] : ""
  };
}

/**
 * Short label for the owner column, given the borrowers array.
 *   one owner  -> "Jeffrey Mason"
 *   two+       -> "Joint: Jeffrey, June"
 */
export function ownersLabel(record, borrowers) {
  const owners = getOwners(record);
  const names = owners
    .map((i) => borrowers[parseInt(i, 10)])
    .filter(Boolean)
    .map((b) => borrowerFullName(b));

  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  return `Joint: ${names.join(", ")}`;
}
